import { useState, useEffect, useCallback, useRef } from 'react';
import {
  NavigationType,
  useLocation,
  useNavigate,
  useNavigationType,
  useSearchParams,
} from 'react-router-dom';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import {
  isStructuredSession,
  OFFICE_HOURS_SESSION_IDS,
  OFFICE_HOURS_SUBSESSION_IDS,
  LUBAV_SUPERSESSIONS,
  LubavOfficeHoursSubsessionType,
  StructuredSessionId,
} from '@backrs_ui/grow-ui/src/models/models';
import { showSnackbar } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { resetBoxData } from '@backrs_ui/grow-ui/src/redux/boxData/boxData';
import {
  addOfficeHoursMessage,
  resetLubavMessages,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav';
import {
  officeHoursApi,
  useGetOfficeHoursSessionDataQuery,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import {
  initiateSession,
  loadOfficeHoursSessionData,
  resetOfficeHoursSession,
  resetStreamingState,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.thunk';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  setActiveSessionType,
  setOfficeHoursSubsessionType,
  setPendingStructuredChoice,
  setPreviewedSession,
  setLoadingSessionData,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState';
import {
  getActiveLubavSession,
  getPendingStructuredChoice,
  getSessionState,
  createSessionAction,
  isShellManagedSession,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  getSeedStatus,
  getSessionStatusEntry,
  getStructuredTypeBySessionId,
} from '@backrs_ui/grow-ui/src/redux/sessionStatuses/sessionStatuses';
import { determineActiveSession } from '@backrs_ui/grow-ui/src/utils/determineActiveSession';

import {
  SHARED_OFFICE_HOURS_SESSION_PATH,
  SHARED_SOLUTION_SESSION_PATH,
} from './sharedSessionPaths';

const isOfficeHoursSubsession = (v: string): v is LubavOfficeHoursSubsessionType =>
  (Object.values(OFFICE_HOURS_SUBSESSION_IDS) as readonly string[]).includes(v);

const isSharedSessionPath = (pathname: string) =>
  SHARED_SOLUTION_SESSION_PATH.test(pathname) || SHARED_OFFICE_HOURS_SESSION_PATH.test(pathname);

const isPageManagedSessionPath = (pathname: string) =>
  pathname.startsWith('/solution-session/') ||
  pathname.startsWith('/coaching-dashboard') ||
  isSharedSessionPath(pathname);

export const useShellSession = (showLubav: boolean) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { isUserOrGuest, loggedIn } = useAuth();

  const [hasClearedSession, setHasClearedSession] = useState(false);
  const prevActiveSessionRef = useRef<string | null>(null);
  // The session the decision effect has already dispatched a load/init for. Held until
  // sessionId lands so a re-render while the request is in flight can't start a second
  // session; reset handlers clear it so a deliberate restart re-resolves.
  const sessionDecisionRef = useRef<string | null>(null);

  const isPageManagedSession = isPageManagedSessionPath(location.pathname);
  // Shared transcripts are read-only views; new chat doesn't apply there.
  const isSharedSessionRoute = isSharedSessionPath(location.pathname);

  const activeSession = useAppSelector(getActiveLubavSession);
  const skipSessionInit = isPageManagedSession || !showLubav;

  const [searchParams] = useSearchParams();
  const sessionIdParam = searchParams.get('session_id');
  const parsedSessionId = sessionIdParam ? parseInt(sessionIdParam, 10) : NaN;
  const querySessionId = Number.isInteger(parsedSessionId) ? parsedSessionId : null;

  // Mount-time id; the shell never unmounts across in-app nav, so a back/forward leaves it pinned.
  const initialSessionIdRef = useRef(querySessionId);

  // Recall a URL session_id only for a signed-in user, and never when a back/forward changed it
  // (that would reload a past conversation) — a refresh keeps the mount-time id, so it still recalls.
  const urlRecallSessionId =
    loggedIn &&
    querySessionId !== null &&
    (querySessionId === initialSessionIdRef.current || navigationType !== NavigationType.Pop)
      ? querySessionId
      : null;

  // Resolve a recalled session's structured type from the seeded statuses before its data loads,
  // so the FEATURED tab and output skeleton show while recall is in flight.
  const recalledStructuredType = useAppSelector((state) =>
    getStructuredTypeBySessionId(state, urlRecallSessionId),
  );

  const shellSession = skipSessionInit
    ? null
    : (recalledStructuredType ?? determineActiveSession(location.pathname, activeSession));

  useEffect(() => {
    if (skipSessionInit) return;

    dispatch(setActiveSessionType(shellSession));
  }, [shellSession, skipSessionInit, dispatch]);

  const OFFICE_HOURS_SESSION_ID_VALUES = Object.values(OFFICE_HOURS_SESSION_IDS) as string[];
  const isOfficeHoursSessionId =
    shellSession !== null && OFFICE_HOURS_SESSION_ID_VALUES.includes(shellSession);

  useEffect(() => {
    if (prevActiveSessionRef.current !== shellSession) {
      if (prevActiveSessionRef.current !== null) {
        setHasClearedSession(false);
        dispatch(setOfficeHoursSubsessionType(null));
      }

      prevActiveSessionRef.current = shellSession;
    }
  }, [shellSession, dispatch]);

  // check if this is a structured session type that supports auto-loading
  const isStructured = isStructuredSession(shellSession);

  // prefetched status tells us whether there's an existing session to resume, and its id
  const shellSessionStatus = useAppSelector((state) =>
    isStructured
      ? getSessionStatusEntry(state, shellSession as StructuredSessionId)?.status
      : undefined,
  );
  const shellSessionMostRecentId = useAppSelector((state) =>
    isStructured
      ? (getSessionStatusEntry(state, shellSession as StructuredSessionId)?.sessionId ?? null)
      : null,
  );
  const hasExistingStructuredSession =
    shellSessionStatus === 'mid' || shellSessionStatus === 'complete';

  const statusesReady = useAppSelector(getSeedStatus) === 'seeded';

  // while this session is awaiting the user's start/continue choice, don't auto-load or initiate it
  const pendingStructuredChoice = useAppSelector(getPendingStructuredChoice);
  const awaitingChoice = pendingStructuredChoice === shellSession;

  // The session to recall by id: an explicit ?session_id, or the most-recent session of this
  // structured type from the seeded statuses. A fresh start/restart resolves to nothing.
  const autoLoadSessionId =
    isStructured && !hasClearedSession && !awaitingChoice && hasExistingStructuredSession
      ? shellSessionMostRecentId
      : null;
  const recallSessionId = urlRecallSessionId ?? autoLoadSessionId;

  const {
    currentData: sessionData,
    error: sessionRecallError,
    isFetching: isFetchingRecall,
  } = useGetOfficeHoursSessionDataQuery(
    { session_id: recallSessionId ?? 0 },
    { skip: !recallSessionId || !shellSession },
  );

  // Hold the shell on a loader while recalling a session whose type we can't yet classify: the
  // seeded statuses haven't loaded and its data hasn't arrived. Avoids flashing page content
  // before switching to the FEATURED output tab.
  const isResolvingRecall = urlRecallSessionId !== null && !statusesReady && !sessionData;

  const sessionId = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav.sessionId,
  );
  // sessionId of the session the shell is resolving, which can briefly differ from the
  // active session mid-switch; the decision effect keys off this rather than sessionId.
  const shellSessionId = useAppSelector(
    (state) => getSessionState(state, shellSession).coachLubav.sessionId,
  );
  const { loading: isTyping, streaming: isStreaming } = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav,
  );
  const sessionTitle = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav.sessionTitle,
  );

  const handleNewChat = useCallback(async () => {
    if (!activeSession || !isShellManagedSession(activeSession) || !isUserOrGuest) {
      return;
    }

    try {
      setHasClearedSession(true);
      sessionDecisionRef.current = null;
      dispatch(setPreviewedSession(null));
      await dispatch(resetOfficeHoursSession({ activeSession })).unwrap();
      dispatch(setOfficeHoursSubsessionType(null));
      dispatch(officeHoursApi.util.invalidateTags(['SessionsList']));

      // New chat exits a structured session back to office hours
      if (isStructuredSession(activeSession)) {
        dispatch(setActiveSessionType(LUBAV_SUPERSESSIONS.OFFICE_HOURS));
      }

      navigate(window.location.pathname, { replace: true });
      await dispatch(resetStreamingState({ activeSession })).unwrap();
    } catch (error) {
      console.error('Error resetting chat session:', error);
      setHasClearedSession(false);
    }
  }, [activeSession, isUserOrGuest, dispatch, navigate]);

  // Clear the current structured session so the shell re-initiates a fresh one;
  // hasClearedSession keeps the most-recent session from reloading.
  const handleRestartStructuredSession = useCallback(() => {
    if (!activeSession || !isShellManagedSession(activeSession) || !isUserOrGuest) {
      return;
    }

    setHasClearedSession(true);
    sessionDecisionRef.current = null;
    dispatch(setPreviewedSession(null));
    dispatch(resetOfficeHoursSession({ activeSession }));
    dispatch(resetStreamingState({ activeSession }));
    dispatch(setOfficeHoursSubsessionType(null));
    dispatch(officeHoursApi.util.invalidateTags(['SessionsList']));
  }, [activeSession, isUserOrGuest, dispatch]);

  // Leave the active session and start a fresh office hours session, navigating to
  // `destination` (defaults to the current route). The state changes are dispatched
  // together so the old session is never momentarily active with a cleared id.
  const handleNewOfficeHoursSession = useCallback(
    (destination?: string) => {
      if (!activeSession || !isShellManagedSession(activeSession) || !isUserOrGuest) {
        return;
      }

      setHasClearedSession(true);
      sessionDecisionRef.current = null;
      dispatch(setPreviewedSession(null));
      dispatch(setActiveSessionType(LUBAV_SUPERSESSIONS.OFFICE_HOURS));
      dispatch(resetOfficeHoursSession({ activeSession }));
      dispatch(resetOfficeHoursSession({ activeSession: LUBAV_SUPERSESSIONS.OFFICE_HOURS }));
      dispatch(resetStreamingState({ activeSession }));
      dispatch(setOfficeHoursSubsessionType(null));
      dispatch(officeHoursApi.util.invalidateTags(['SessionsList']));

      navigate(destination ?? window.location.pathname, { replace: true });
    },
    [activeSession, isUserOrGuest, dispatch, navigate],
  );

  // redirect to 404 for invalid office hours session types (e.g. /office-hours/invalidId)
  const isOfficeHoursRoute = location.pathname.startsWith('/office-hours/');

  useEffect(() => {
    if (isOfficeHoursRoute && shellSession && !isShellManagedSession(shellSession)) {
      navigate(APP_ROUTE.NOT_FOUND_404);
    }
  }, [isOfficeHoursRoute, shellSession, navigate]);

  // Resolve the shell session exactly once: a structured session with an existing record
  // resumes it, otherwise (or for a supersession) a fresh session is started. The query
  // must settle before deciding, and sessionDecisionRef latches the choice until sessionId
  // lands so an in-flight request can't be joined by a second load or initiate.
  useEffect(() => {
    if (!shellSession || !isUserOrGuest || urlRecallSessionId) return;

    // awaiting the user's start/continue choice — leave the session untouched until they pick
    if (awaitingChoice) return;

    // not a structured session or a supersession — nothing to resolve here
    if (!isShellManagedSession(shellSession)) return;

    // already resolved — drop the latch and stop
    if (shellSessionId) {
      sessionDecisionRef.current = null;

      return;
    }

    // a load/initiate for this session is already in flight
    if (sessionDecisionRef.current === shellSession) return;

    // a structured session can resume an existing record; a restart (hasClearedSession)
    // or a supersession always starts fresh
    if (isStructured && !hasClearedSession) {
      // decide load-vs-initiate from the seeded statuses, so wait for the initial seed
      if (!statusesReady) return;

      if (autoLoadSessionId) {
        if (sessionData === undefined) {
          // not settled yet — keep waiting, unless the load errored, in which case start
          // fresh rather than leave the session blank with no recovery
          if (!sessionRecallError) return;

          console.error(
            'useShellSession: existing session load failed; starting a fresh session',
            sessionRecallError,
          );
        } else if (sessionData.session_id === autoLoadSessionId) {
          sessionDecisionRef.current = shellSession;
          dispatch(loadOfficeHoursSessionData({ sessionData, activeSession: shellSession }));

          if (sessionData.subsession_type && isOfficeHoursSubsession(sessionData.subsession_type)) {
            dispatch(setOfficeHoursSubsessionType(sessionData.subsession_type));
          }

          return;
        } else {
          // cached data is still for the previous session — wait for this one to load
          return;
        }
      }
      // no existing session (status 'none') or the load failed — start a fresh one
    }

    sessionDecisionRef.current = shellSession;
    dispatch(createSessionAction(resetLubavMessages, shellSession)(undefined));
    dispatch(createSessionAction(resetBoxData, shellSession)(undefined));

    dispatch(
      initiateSession({
        path: window.location.pathname,
        officeHoursId: shellSession,
        activeSession: shellSession,
      }),
    )
      .unwrap()
      .catch(() => {
        sessionDecisionRef.current = null;
        dispatch(
          createSessionAction(
            addOfficeHoursMessage,
            shellSession,
          )({
            text: appStrings.modules.chatBox.overloadErr,
            sender: 'ai',
          }),
        );
      });
  }, [
    dispatch,
    isUserOrGuest,
    shellSession,
    shellSessionId,
    urlRecallSessionId,
    isStructured,
    hasClearedSession,
    awaitingChoice,
    statusesReady,
    autoLoadSessionId,
    sessionData,
    sessionRecallError,
  ]);

  // load recalled session data
  useEffect(() => {
    if (shellSession && urlRecallSessionId) {
      if (sessionRecallError) {
        console.error('useShellSession: session recall failed', {
          urlRecallSessionId,
          shellSession,
          error: sessionRecallError,
        });
        dispatch(
          showSnackbar({ message: appStrings.modules.chatBox.recallErr, severity: 'error' }),
        );
        navigate(APP_ROUTE.HOME);

        return;
      }

      if (sessionData && sessionData.session_id !== urlRecallSessionId) {
        return;
      }

      if (sessionData) {
        const recalledSession = isStructuredSession(sessionData.session_type)
          ? sessionData.session_type
          : shellSession;

        if (recalledSession !== activeSession) {
          dispatch(setActiveSessionType(recalledSession));
        }

        dispatch(loadOfficeHoursSessionData({ sessionData, activeSession: recalledSession }));

        if (sessionData.subsession_type && isOfficeHoursSubsession(sessionData.subsession_type)) {
          dispatch(setOfficeHoursSubsessionType(sessionData.subsession_type));
        }
      }
    }
  }, [
    sessionData,
    shellSession,
    activeSession,
    urlRecallSessionId,
    dispatch,
    navigate,
    sessionRecallError,
  ]);

  // clear a stale pending choice once the user has moved off that session
  useEffect(() => {
    if (pendingStructuredChoice && pendingStructuredChoice !== shellSession) {
      dispatch(setPendingStructuredChoice(null));
    }
  }, [pendingStructuredChoice, shellSession, dispatch]);

  // Any navigation abandons a preview -- including a same-page session recall, which changes only
  // the session_id param.
  useEffect(() => {
    dispatch(setPreviewedSession(null));
  }, [location.pathname, sessionIdParam, dispatch]);

  // Mark that an existing session's saved data is loading. The recall query only runs when there's
  // a session to load (explicit ?session_id or the most-recent structured session), so this one
  // flag covers both paths.
  useEffect(() => {
    const loadingExisting = !!shellSession && isFetchingRecall;

    dispatch(setLoadingSessionData(loadingExisting ? shellSession : null));
  }, [shellSession, isFetchingRecall, dispatch]);

  return {
    activeSession,
    isSharedSessionRoute,
    handleNewChat,
    handleRestartStructuredSession,
    handleNewOfficeHoursSession,
    isTyping,
    isStreaming,
    isOfficeHoursSessionId,
    isStructured,
    isResolvingRecall,
    sessionId,
    sessionTitle,
  };
};
